import cx from 'clsx';
import { motion, useReducedMotion } from 'motion/react';
import { forwardRef, useRef } from 'react';
import {
  Button as AriaButton,
  Disclosure as AriaDisclosure,
  DisclosurePanel as AriaDisclosurePanel,
  type DisclosureProps as AriaDisclosureProps,
} from 'react-aria-components';
import { IconChevronBottomOutlined } from '@repo/monochrome-icons';
import styles from './accordion.module.css';

type BaseAccordionSingleProps = {
  children: React.ReactNode;
  className?: string;
  onExpandComplete?: () => void;
} & (
  | { title: string; icon?: React.ReactNode; header?: never }
  | { title?: never; icon?: never; header: JSX.Element }
);

export type AccordionSingleProps = BaseAccordionSingleProps & AriaDisclosureProps;

const AnimatedDisclosurePanel = motion.create(AriaDisclosurePanel);

export const AccordionSingle = forwardRef<HTMLDivElement, AccordionSingleProps>(
  function AccordionSingle(
    { title, icon, header, children, className, onExpandComplete, ...props }: AccordionSingleProps,
    ref
  ): React.ReactElement {
    const panelRef = useRef<HTMLDivElement>(null);
    const shouldReduceMotion = useReducedMotion();

    return (
      <AriaDisclosure className={cx(styles.accordion, className)} ref={ref} {...props}>
        {({ isExpanded }) => (
          <>
            <div className={styles.accordion__header}>
              <AriaButton
                className={styles.accordion__header__clickable}
                data-testid="accordion-trigger"
                preventFocusOnPress
                slot="trigger"
              >
                {title ? (
                  <div className={cx('body-prominent', styles.accordion__label)}>
                    {icon ? <span className={styles.accordion__label_icon}>{icon}</span> : null}
                    {title}
                  </div>
                ) : null}
                {header ? header : null}
                <IconChevronBottomOutlined aria-hidden="true" className={styles.accordion__arrow} />
              </AriaButton>
            </div>
            <AnimatedDisclosurePanel
              animate={{
                opacity: isExpanded ? 1 : 0,
                height: isExpanded ? 'auto' : 0,
              }}
              className={styles.accordion__content}
              initial={false}
              onAnimationComplete={() => {
                panelRef.current?.style.setProperty(
                  'content-visibility',
                  isExpanded ? 'visible' : 'hidden'
                );
                if (isExpanded) onExpandComplete?.();
              }}
              onAnimationStart={() => {
                panelRef.current?.style.setProperty('content-visibility', 'visible');
              }}
              ref={panelRef}
              style={{ display: 'block' }}
              transition={{ duration: shouldReduceMotion ? 0 : 0.25, ease: 'easeInOut' }}
            >
              <div className={styles.accordion__content__inner} data-testid="accordion-content">
                {children}
              </div>
            </AnimatedDisclosurePanel>
          </>
        )}
      </AriaDisclosure>
    );
  }
);
